/**
 * @module SectionManager
 * @description Canonical HTML response pipeline for section and document fetches.
 * Owns section URL building, cache policy, abort-aware fetches, parsed-document helpers,
 * and section patch handoff.
 * @requires @fc/section-response
 */
import {
  buildSectionDomId,
  createAbortError,
  findSectionNode,
  isAbortError,
  LatestRequestController,
  patchHTML,
  parseHTMLDocument,
} from '@fc/section-response';

const PREFIX = 'shopify-section-';

function sectionUrl(id, base = new URL(window.location.href)) {
  const url = normalizeUrl(base);
  url.searchParams.set('section_id', stripPrefix(id));
  url.searchParams.sort();
  return url.toString();
}

function stripPrefix(id) {
  return id.startsWith(PREFIX) ? id.slice(PREFIX.length) : id;
}

function normalizeUrl(url) {
  if (url instanceof URL) {
    return new URL(url.toString());
  }

  return new URL(String(url || window.location.href), window.location.href);
}

function normalizeFetchOptions(options = {}) {
  return {
    cache: options.cache ?? true,
    signal: options.signal,
  };
}

function normalizeSectionOptions(cacheOrOptions = true, url = new URL(window.location.href)) {
  if (typeof cacheOrOptions === 'object' && cacheOrOptions !== null && !(cacheOrOptions instanceof URL)) {
    return {
      cache: cacheOrOptions.cache ?? true,
      signal: cacheOrOptions.signal,
      url: normalizeUrl(cacheOrOptions.url ?? window.location.href),
    };
  }

  return {
    cache: cacheOrOptions ?? true,
    signal: undefined,
    url: normalizeUrl(url),
  };
}

function hasShadow(el) {
  if (el.shadowRoot) return true;
  return [...el.children].some(hasShadow);
}

export function buildSectionSelector(id) {
  return buildSectionDomId(id);
}

export function normalizeSectionId(id) {
  return stripPrefix(id);
}

export async function updateSection(id, html) {
  const sel = buildSectionSelector(id);
  const prev = document.getElementById(sel);
  const next = findSectionNode(html, id);

  if (!prev) throw new Error(`Section ${id} not found`);
  if (!next) throw new Error(`Section ${id} missing from render response`);

  patchHTML(prev, html, next);
}

class SectionManager {
  #html = new Map();
  #inflight = new Map();
  #renderControllers = new Map();

  constructor() {
    window.addEventListener('load', () => this.#warmCache());
  }

  async renderSection(id, opts) {
    const request = this.#getRenderController(id).start();
    const useCache = opts?.cache ?? !Shopify.designMode;

    try {
      const markup = await this.getSectionHTML(id, {
        cache: useCache,
        signal: request.signal,
        url: opts?.url,
      });

      if (request.isCurrent()) {
        updateSection(id, markup);
      }

      return markup;
    } catch (error) {
      if (isAbortError(error)) {
        return null;
      }

      throw error;
    } finally {
      request.finish();
    }
  }

  async getHTML(url, options = {}) {
    const requestUrl = normalizeUrl(url).toString();
    const { cache, signal } = normalizeFetchOptions(options);

    if (signal?.aborted) {
      throw createAbortError();
    }

    if (cache) {
      const hit = this.#html.get(requestUrl);
      if (hit) return hit;
    }

    if (!signal) {
      const pending = this.#inflight.get(requestUrl);
      if (pending) return pending;
    }

    const request = fetch(requestUrl, {
      cache: cache ? 'default' : 'no-store',
      signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTML fetch failed: ${response.status}`);
        }

        const html = await response.text();
        if (signal?.aborted) {
          throw createAbortError();
        }

        if (cache) {
          this.#html.set(requestUrl, html);
        }

        return html;
      })
      .catch((err) => {
        if (!signal) {
          this.#inflight.delete(requestUrl);
        }
        throw err;
      });

    if (!signal) {
      this.#inflight.set(requestUrl, request);
    }

    const html = await request;

    if (!signal) {
      this.#inflight.delete(requestUrl);
    }

    return html;
  }

  async getHTMLDocument(url, options = {}) {
    const html = await this.getHTML(url, options);
    return parseHTMLDocument(html);
  }

  async getSectionHTML(id, cache = true, url = new URL(window.location.href)) {
    const options = normalizeSectionOptions(cache, url);
    return this.getHTML(sectionUrl(id, options.url), options);
  }

  async getSectionDocument(id, cache = true, url = new URL(window.location.href)) {
    const options = normalizeSectionOptions(cache, url);
    return this.getHTMLDocument(sectionUrl(id, options.url), options);
  }

  #warmCache() {
    for (const el of document.querySelectorAll('.shopify-section')) {
      const key = sectionUrl(el.id);
      if (this.#html.has(key) || hasShadow(el)) continue;
      this.#html.set(key, el.outerHTML);
    }
  }

  #getRenderController(id) {
    const normalized = normalizeSectionId(id);
    if (!this.#renderControllers.has(normalized)) {
      this.#renderControllers.set(normalized, new LatestRequestController());
    }

    return this.#renderControllers.get(normalized);
  }
}

export const sectionManager = new SectionManager();
